import { useMemo } from "react";
import { useAppData } from "#app/lib/data";
import type { EntityItemConfig, EntityPoolConfig } from "@salestrak/ui";
import { getEntityGlyph, type EntityData } from "#app/lib/entity-visuals";
import { poolItemVisual } from "../utils";
import { canEdit } from "#app/lib/utils/access-level";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = "workflows";

export type UseWorkflowPoolOptions = Omittable<{
  /** Workflow IDs to exclude from the pool (e.g., the current workflow) */
  exclude?: string[];
  /** When true, drop workflows the viewer can't edit. Use for
   *  create-issue dialogs (sidebar + issue-view create dialog) so
   *  users can't pick a workflow the createIssue resolver would
   *  reject. Defaults to false so existing read-only consumers
   *  (filters, etc.) still see every visible workflow. */
  editableOnly?: boolean;
}>;

/**
 * Returns an EntityPoolConfig for workflows using data already available
 * in the global AppDataContext (no extra query needed).
 */
export function useWorkflowPool(options?: UseWorkflowPoolOptions) {
  const { exclude, editableOnly = false } = options ?? {};
  const { workflows, isLoading: loading } = useAppData();

  const excludeSet = useMemo(
    () => (exclude?.length ? new Set(exclude) : null),
    [exclude],
  );

  const items = useMemo<EntityItemConfig[]>(() => {
    if (!workflows) return [];
    const filtered = editableOnly
      ? workflows.filter((w) => canEdit(w.viewerAccessLevel))
      : workflows;
    const mapped = filtered.map((w) => {
      const entity: EntityData = {
        kind: "workflow",
        id: w.workflowId,
        workflow: w,
      };
      // `render` drives the display; the primitive icon/color stay as data so
      // selections round-trip through selectionToIconEntity.
      const glyph = getEntityGlyph(entity);
      return {
        id: w.workflowId,
        title: w.name ?? "Untitled workflow",
        avatar: {
          icon: glyph.icon ?? undefined,
          color: glyph.color ?? undefined,
          padded: true,
          render: () => poolItemVisual(entity),
        },
        description: w.isDefault ? "Default" : undefined,
        data: { isDefault: w.isDefault },
      };
    });
    return excludeSet ? mapped.filter((item) => !excludeSet.has(item.id)) : mapped;
  }, [workflows, excludeSet, editableOnly]);

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Workflows",
      items,
    }),
    [items],
  );

  return { pool, loading };
}

export { POOL_ID as WORKFLOW_POOL_ID };
